<script setup lang="ts">
import type { ProductItemDetail } from '#layers/base/server/utils/product-details/domain'
import type { CampaignProduct } from '~/types'
import type { CampaignDiscount, CampaignProductItem } from '~/composables/useCampaignProductGroups'
import IconInformation from '~icons/mdi/information-outline'
import type { AlternativesEntry } from '~/composables/useAlternativesEntry'
import type { AlternativesNavigationTarget } from '~/composables/useAlternativesUrl'
import ProductDetail from '~/components/product/ProductDetail.vue'

const props = defineProps<{
  product: CampaignProduct | null
  listIndex?: number
  isSponsored?: boolean
  hasDifferentSupplier?: boolean
}>()

function toAlternativesEntry(
  value: AlternativesEntry | AlternativesNavigationTarget | null | undefined,
): AlternativesEntry | null {
  if (!value) return null
  if ('mode' in value) return value
  return { mode: 'navigate', target: value }
}

// GA4 `index` is 1-based (position in the product list).
const trackingIndex = computed(() => (props.listIndex ?? 0) + 1)

const isAlternativeSheet = inject<boolean>('isAlternativeSheet', false)
const sponsoredTrack = inject<ComputedRef<{
  onImpression: (productHandle: string) => void
  onClick: (productHandle: string) => void
  onAddToCart: (productHandle: string) => void
} | null> | null>('martailerSponsoredTrack', null)
const sponsoredInView = inject<Ref<boolean>>('martailerSponsoredInView', ref(false))
const isHybridSearch = useIsHybridSearch()
// Override from useAlternativesTrackingSection while the alternatives sheet is open.
const { section: alternativesTrackingSection } = useAlternativesTrackingSection()
const trackingPageSection = computed(() => alternativesTrackingSection.value || undefined)

const { isHumanMedicineRestricted } = useHumanMedicineRestriction()
const { getDiscountFlagPercent } = useDiscountFlag()
const { trackPdpViewItem } = usePdpViewItemTracking()
const isScalePriceSheetOpen = ref(false)
const isOrderOptimizationOpen = ref(false)
const { currencyToString, resolveItemVariant } = useHelper()
const pageSection = usePageSection()
const { trackAddToShoppingList, trackRemoveFromShoppingList, trackCreateCustomShoppingList } = useShoppingListTracking()
const { trackViewItem, getTrackingPriceEuros } = useProductTracking()
const gtm = useGtm()
const productCardRef = ref<{ $el?: HTMLElement } | null>(null)
const {
  quantity,
  reference,
  clinicChain,
  currentPrice,
  currentPriceCents,
  selectedProduct,
  isFavourite,
  isSubscribed,
  configurations,
  changeSelected,
  selectedProductSupplier,
  productDetailUrl,
  updateSelectedReferenceName,
} = useProductStateManagement(props.product!)

const {
  updateQuantity,
  updateReminder,
  faveUpdate,
} = useProductActionsManagement()

const productName = computed(() => props.product?.name ?? selectedProduct.value.name ?? '')

const manufacturer = computed(() => {
  const value = selectedProduct.value.manufacturer
  if (!value || typeof value === 'string') {
    return value ? { handle: '', name: value } : undefined
  }
  return { handle: value.handle ?? '', name: value.name ?? '', imageUrl: 'imageUrl' in value ? value.imageUrl : undefined }
})

function toTrackingProduct(product: CampaignProductItem) {
  return {
    handle: product.handle,
    name: productName.value,
    manufacturer: manufacturer.value,
  }
}

const isProductRestricted = computed(() =>
  isHumanMedicineRestricted(selectedProduct.value.detail?.saleDelimitations ?? selectedProduct.value.saleDelimitations),
)

function isPdpItem(item: CampaignProductItem): item is CampaignProductItem & { detail: ProductItemDetail } {
  return item.detail != null
}

const detailItem = computed(() => isPdpItem(selectedProduct.value) ? selectedProduct.value : null)
const showDetail = computed(() => detailItem.value != null)
const discountFlagPercent = computed(() =>
  getDiscountFlagPercent(selectedProductSupplier.value?.handle),
)

useProductListPositionTracking({
  enabled: () => !showDetail.value,
  cardRef: productCardRef,
  getIdExternal: () => selectedProduct.value.idExternal,
  getPosition: () => trackingIndex.value,
})

function trackExploreAlternatives() {
  if (!selectedProduct.value) return
  const alternatives = toAlternativesEntry(selectedProduct.value.alternatives)
  const atcCode = alternatives?.mode === 'overlay'
    ? alternatives.source.atcvetCodes[0] ?? ''
    : ''

  gtm?.trackEvent({
    event: 'explore_product_alternatives',
    page_section: trackingPageSection.value || pageSection,
    item_id: selectedProduct.value.handle,
    item_name: props.product?.name ?? '',
    supplier_name: selectedProductSupplier.value?.name ?? '',
    atc_code: atcCode,
  })
}

const handleFaveChange = async (value: boolean | undefined) => {
  if (value === undefined) return
  const wasFavourite = isFavourite.value
  await faveUpdate(value, selectedProduct.value.handle, wasFavourite)
  isFavourite.value = value
  trackViewItem({
    product: toTrackingProduct(selectedProduct.value),
    price: getTrackingPriceEuros(selectedProduct.value, currentPriceCents.value),
    selectedFields: selectedProduct.value.fields,
    supplierName: selectedProductSupplier.value?.name,
  })
  if (wasFavourite && !value) {
    trackRemoveFromShoppingList({
      product: { handle: selectedProduct.value.handle, name: props.product?.name },
      supplierName: selectedProductSupplier.value?.name,
      pageSection,
    })
  }
}

function handleSavedToList({ listName, isNewList, quantity }: { listName: string, isNewList: boolean, quantity: number }) {
  if (isNewList) {
    trackCreateCustomShoppingList({ pageSection })
  }
  const selectedProductWithName = { ...selectedProduct.value, name: productName.value }
  trackAddToShoppingList({
    products: [selectedProductWithName],
    listName,
    fields: selectedProduct.value.fields,
    reference: reference.value,
    supplierName: selectedProductSupplier.value?.name,
    quantity,
    pageSection,
  })
}

function trackSelectProductVariant(variantType: string) {
  if (!selectedProduct.value) return
  gtm?.trackEvent({
    event: 'select_product_variant',
    page_section: trackingPageSection.value || pageSection,
    item_id: selectedProduct.value.handle,
    item_name: props.product?.name ?? '',
    supplier_name: selectedProductSupplier.value?.name ?? '',
    variant_type: variantType,
  })
}

function handleVariantChange(key: string, value: string) {
  changeSelected(key, value)
  trackSelectProductVariant(key)
}

function trackSponsoredImpression() {
  const handle = selectedProduct.value?.handle
  if (handle) sponsoredTrack?.value?.onImpression(handle)
}

function trackSponsoredAddToCart() {
  const handle = selectedProduct.value?.handle
  if (handle) sponsoredTrack?.value?.onAddToCart(handle)
}

// Martailer tracking click only for PDP navigations
function trackSponsoredClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null
  if (!target?.closest('[data-nav-id="product_title_link"], [data-nav-id="product_image"]')) return
  const handle = selectedProduct.value?.handle
  if (handle) sponsoredTrack?.value?.onClick(handle)
}

// Impression for the selected variant when the tile is in view; again when the variant changes.
watch(
  [() => selectedProduct.value?.handle, sponsoredInView],
  ([handle, inView]) => {
    if (!props.isSponsored || !inView || !handle) return
    trackSponsoredImpression()
  },
)

async function handleUpdateQuantity(value: number) {
  const previousQuantity = quantity.value
  const success = await updateQuantity(selectedProductSupplier.value?.handle ?? '', selectedProduct.value.handle, reference.value, value, previousQuantity, { index: trackingIndex.value, pageSection: trackingPageSection.value })
  if (success) {
    quantity.value = value
    // Martailer addtocart: first add only (ignore later +/- / remove).
    if (previousQuantity === 0 && value > 0) trackSponsoredAddToCart()
  }
  // Skip follow-up view_item in the alternatives sheet — add_to_cart already covers it.
  if (isAlternativeSheet) return
  trackViewItem({
    product: { ...toTrackingProduct(selectedProduct.value), quantity: value },
    price: getTrackingPriceEuros(selectedProduct.value, currentPriceCents.value),
    selectedFields: selectedProduct.value.fields,
    supplierName: selectedProductSupplier.value?.name,
  })
}

async function handleScaleToCart(scaleQuantity: number): Promise<void> {
  const previousQuantity = quantity.value
  const success = await updateQuantity(selectedProductSupplier.value?.handle ?? '', selectedProduct.value.handle, reference.value, scaleQuantity, previousQuantity, { index: trackingIndex.value, pageSection: trackingPageSection.value })
  if (success) {
    quantity.value = scaleQuantity
    if (previousQuantity === 0 && scaleQuantity > 0) trackSponsoredAddToCart()
  }
  isScalePriceSheetOpen.value = false
  if (isAlternativeSheet) return
  trackViewItem({
    product: { ...toTrackingProduct(selectedProduct.value), quantity: scaleQuantity },
    price: getTrackingPriceEuros(selectedProduct.value, currentPriceCents.value),
    selectedFields: selectedProduct.value.fields,
    supplierName: selectedProductSupplier.value?.name,
  })
}

function handleScalePriceSheetToggle() {
  const willOpen = !isScalePriceSheetOpen.value
  isScalePriceSheetOpen.value = willOpen

  if (!willOpen) return

  gtm?.trackEvent({
    event: 'view_bulk_pricing',
    page_section: trackingPageSection.value || pageSection,
    item_id: selectedProduct.value.handle,
    item_name: props.product?.name ?? '',
    supplier_name: selectedProductSupplier.value?.name ?? '',
  })
}

function handleOrderOptimizationToggle() {
  const willOpen = !isOrderOptimizationOpen.value
  isOrderOptimizationOpen.value = willOpen

  if (!willOpen) return

  gtm?.trackEvent({
    event: 'view_order_optimization',
    page_section: trackingPageSection.value || pageSection,
    item_id: selectedProduct.value.handle,
    item_name: props.product?.name ?? '',
    supplier_name: selectedProductSupplier.value?.name ?? '',
  })
}

function handleUpdateReminder(value: boolean) {
  updateReminder(selectedProduct.value.handle, !value)
  isSubscribed.value = !isSubscribed.value

  if (!value) return // only track when subscribing (reminder turned on)
  gtm?.trackEvent({
    event: 'subscribe_product_availability_reminder',
    page_section: trackingPageSection.value || pageSection,
    item_id: selectedProduct.value.handle,
    item_name: props.product?.name ?? '',
    item_variant: resolveItemVariant(selectedProduct.value.fields),
    supplier_name: selectedProductSupplier.value?.name ?? '',
  })
}

// necessary to display the supplier data in the add to favorite dialog
function mergeSupplierToProduct(product: CampaignProductItem): CampaignProductItem {
  return {
    ...product,
    name: productName.value,
    supplier: selectedProductSupplier.value
      ? {
          handle: selectedProductSupplier.value.handle,
          name: selectedProductSupplier.value.name,
          imageUrl: selectedProductSupplier.value.imageUrl,
        }
      : product.supplier,
  }
}

const hasReachedScalePrices = computed(() => {
  return selectedProduct.value?.scalePrices?.some((scale) => {
    if (quantity.value === 0) {
      return scale.minQuantity === 1
    }
    return quantity.value >= scale.minQuantity
  }) ?? false
})

// TODO: Remove this once we replace the old search product data with the new one
function campaignPrices(campaign?: CampaignDiscount) {
  if (!campaign) return undefined
  return {
    name: campaign.name,
    endDate: campaign.endDate,
    status: campaign.status,
    beforePrice: currencyToString(campaign.beforePrice),
    afterPrice: currencyToString(campaign.afterPrice),
    discount: campaign.discountPercent,
    discount_type: campaign.discountType,
    discountPercent: campaign.discountPercent,
  }
}

const imageCampaign = computed(() => campaignPrices(selectedProduct.value.campaign))
const reAvailabilityDate = computed(() =>
  selectedProduct.value.detail?.dateReAvailable
  ?? selectedProduct.value.reAvailabilityDate
  ?? selectedProduct.value.dateReAvailable
  ?? '',
)

watch(
  [showDetail, () => selectedProduct.value?.handle],
  () => {
    if (!showDetail.value || !selectedProduct.value) return
    const product = selectedProduct.value
    const priceCents = product.campaign?.status === 'ACTIVE' && typeof product.campaign.afterPrice === 'number'
      ? product.campaign.afterPrice
      : currentPriceCents.value
    trackPdpViewItem({
      product: {
        handle: product.handle,
        name: productName.value,
        campaign: product.campaign,
        idExternal: product.idExternal,
        categoryIds: product.detail?.categoryIds,
        clinicChain: product.clinicChain,
        clinicChains: product.clinicChains,
      },
      price: priceCents != null ? priceCents / 100 : undefined,
      fields: product.fields,
      supplierName: selectedProductSupplier.value?.name,
      reference: reference.value,
      quantity: quantity.value > 0 ? quantity.value : 1,
    })
  },
  { immediate: true },
)
</script>

<template>
  <ProductDetail
    v-if="detailItem"
    :is-favourite="isFavourite"
    :item="detailItem"
    :clinic-chain="clinicChain"
    :product-name="productName"
    :supplier="selectedProductSupplier"
    :manufacturer="manufacturer"
    :alternatives="toAlternativesEntry(selectedProduct.alternatives)"
    :current-price="currentPrice"
    :campaign="imageCampaign"
    :discount-flag-percent="discountFlagPercent"
    @saved="handleSavedToList"
    @update:is-favourite="(value) => handleFaveChange(value)"
  />
  <v-divider v-if="showDetail" class="alternative-divider" />
  <v-card
    ref="productCardRef"
    flat
    class="pt-5 pb-7 px-5"
    :class="{ 'rounded-t-0': showDetail, 'rounded-b-0': hasDifferentSupplier }"
  >
    <v-row dense>
      <v-col
        v-if="!showDetail"
        cols="12"
        sm="3"
        md="2"
        lg="2"
        data-testid="action-buttons"
      >
        <ProductCardImage
          min-height="180"
          max-height="180"
          :scale-pricing="selectedProduct.scalePricing"
          :is-favourite="isFavourite"
          :img-src="selectedProduct.imageUrl"
          :alt="productName"
          :fallback-src="selectedProductSupplier?.imageUrl"
          :navigation-path="productDetailUrl"
          :product="mergeSupplierToProduct(selectedProduct)"
          :current-price="currentPrice"
          :current-price-cents="currentPriceCents"
          :campaign-after-price-cents="selectedProduct.campaign?.afterPrice"
          :campaign="imageCampaign"
          @update:is-favourite="(value) => handleFaveChange(value)"
          @saved="handleSavedToList"
          @click="trackSponsoredClick"
        />
      </v-col>
      <v-col
        cols="12"
        :sm="showDetail ? '6' : '9'"
        :md="showDetail ? '6' : '3'"
        :lg="showDetail ? '6' : '4'"
      >
        <v-row class="h-100" no-gutters>
          <v-col
            v-if="!showDetail"
            cols="12"
            class="mb-2"
            @click="trackSponsoredClick"
          >
            <ProductHeadlineAndInfo
              :clinic-chain="clinicChain"
              :id-external="selectedProduct.idExternal"
              :product-name="productName"
              :manufacturer="manufacturer"
              :supplier="selectedProductSupplier"
              :navigation-path="productDetailUrl"
            />
          </v-col>
          <v-col cols="12" class="d-flex flex-column mb-2">
            <ProductPriceAndScaleGroup
              :list-price="currencyToString(selectedProduct.listPrice)"
              :current-price="currentPrice"
              :current-price-cents="currentPriceCents"
              :campaign-after-price-cents="selectedProduct.campaign?.afterPrice"
              :campaign="campaignPrices(selectedProduct.campaign)"
              :discount-percent="selectedProduct.campaign?.discountPercent"
              :scale-pricing="selectedProduct.scalePricing"
              :has-scale-prices="selectedProduct.hasScalePrices"
              :reached-scale-prices="hasReachedScalePrices"
              :scale-prices="selectedProduct.scalePrices"
              :selected-quantity="quantity"
              :customer-reference="selectedProduct.references"
              :availability-status="selectedProduct.availability"
              :human-medicine-restricted="isProductRestricted"
              :max-quantity="selectedProduct.orderCondition?.max"
              @scale-prices-handler="handleScalePriceSheetToggle"
              @select-scale-quantity="handleUpdateQuantity"
            />
          </v-col>
          <v-col cols="12" class="d-flex align-center vmz-product-info__third-col mt-auto">
            <ProductAvailabilityAndOrderOptimization
              :availability-best-before="selectedProduct.dateBestBefore ?? ''"
              :re-availability-date="reAvailabilityDate"
              :supplier-img-src="selectedProductSupplier?.imageUrl ?? ''"
              :supplier-name="selectedProductSupplier?.handle ?? ''"
              :availability-status="selectedProduct.availability"
              :last-ordered-quantity="selectedProduct.lastOrderedQuantity"
              @order-optimization-handler="handleOrderOptimizationToggle"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        sm="12"
        :md="showDetail ? '6' : '7'"
        lg="6"
      >
        <v-row class="h-100 ms-8" no-gutters>
          <v-col
            v-if="isSponsored"
            cols="12"
            class="mb-1 d-flex align-center justify-end"
          >
            <span
              class="d-flex align-center ga-1"
              data-test-id="sponsored-label"
            >
              <span class="text-caption text-vm-grey-7">
                {{ $t('sponsored') }}
              </span>
              <v-tooltip location="top" max-width="280">
                <template #activator="{ props: tooltipProps }">
                  <v-icon
                    v-bind="tooltipProps"
                    :icon="IconInformation"
                    size="20"
                    color="vm-grey-7"
                    class="cursor-pointer"
                    :aria-label="$t('sponsored.tooltip')"
                  />
                </template>
                <span>{{ $t('sponsored.tooltip') }}</span>
              </v-tooltip>
            </span>
          </v-col>
          <v-col cols="12" class="mb-2 pb-1">
            <v-row no-gutters>
              <template v-for="(values, key) in configurations" :key="key">
                <v-col
                  cols="12"
                  sm="6"
                  lg="12"
                  class="mb-auto pb-2"
                >
                  <p class="text-caption text-vm-grey-7 mb-1">
                    {{ $t(`productGroup.${key}`) }}
                  </p>
                  <v-btn-toggle
                    class="flex-wrap overflow-visible h-auto vmz-btn-toggle"
                    variant="flat"
                    :show-arrows="false"
                    data-test-id="product-variant-toggle"
                    mandatory
                    shaped
                  >
                    <v-btn
                      v-for="value in sortVariants(values)"
                      :key="value"
                      height="40"
                      class="rounded-lg vmz-btn-toggle-btn"
                      :active="selectedProduct.fields[key] === value"
                      data-testid="action-button"
                      data-track-id="product_select_variant"
                      @click="() => handleVariantChange(key, value)"
                    >
                      {{ value }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </template>
            </v-row>
          </v-col>
          <v-col cols="12" class="d-flex justify-end mt-auto">
            <template v-if="selectedProduct.availability === 'NOT_AVAILABLE'">
              <ProductAlternativeAndReminder
                :reminder="isSubscribed"
                :alternatives="toAlternativesEntry(selectedProduct.alternatives)"
                @update:reminder="handleUpdateReminder"
                @explore:alternatives="trackExploreAlternatives"
              />
            </template>
            <template v-else>
              <ProductReferenceAndQuantity
                :quantity-value="quantity"
                :last-ordered-quantity="selectedProduct.lastOrderedQuantity"
                :last-ordered-date="selectedProduct.lastOrderedDate"
                :selected-reference-name="reference"
                :customer-reference="selectedProduct.references"
                :alternatives="toAlternativesEntry(selectedProduct.alternatives)"
                :order-condition="selectedProduct.orderCondition"
                :human-medicine-restricted="isProductRestricted"
                :clinic-chain="clinicChain"
                @update:quantity-value="handleUpdateQuantity"
                @update:selected-reference-name="updateSelectedReferenceName"
                @explore:alternatives="trackExploreAlternatives"
              />
            </template>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
  <ProductScalePriceSheet
    v-if="!isHybridSearch"
    :is-scale-price-sheet-open="isScalePriceSheetOpen"
    :scale-prices="selectedProduct.scalePrices ?? []"
    :scale-add-to-cart="handleScaleToCart"
    :max-quantity="selectedProduct.orderCondition?.max"
    @update:is-scale-price-sheet-open="value => isScalePriceSheetOpen = value"
  />
  <ScaleSheet
    v-else
    :is-scale-price-sheet-open="isScalePriceSheetOpen"
    :scale-prices="selectedProduct.scalePrices ?? []"
    :scale-add-to-cart="async (qty: number) => handleScaleToCart(qty)"
    :max-quantity="selectedProduct.orderCondition?.max"
    @update:is-scale-price-sheet-open="value => isScalePriceSheetOpen = value"
  />
  <v-bottom-sheet v-model="isOrderOptimizationOpen">
    <ProductOrderOptimizationSheet
      :product="selectedProduct"
      :is-open="isOrderOptimizationOpen"
    />
  </v-bottom-sheet>
</template>

<style scoped lang="scss">
.vmz-btn-toggle {
  :deep(.v-btn--active) {
    background-color: rgb(var(--v-theme-vm-grey-2)) !important;
    color: rgb(var(--v-theme-vm-grey-9)) !important;
  }
  &-btn {
    height: 40px !important;
    min-height: 40px;
    :deep(.v-btn__overlay) {
      opacity: 0 !important;
    }
    :deep(.v-btn__content) {
      white-space: normal;
    }
    &:disabled {
      background-color: rgb(var(--v-theme-vm-grey-1)) !important;
      :deep(.v-btn__overlay) {
        opacity: 0 !important;
      }
      :deep(.v-btn__content) {
        color: rgb(var(--v-theme-vm-grey-4)) !important;
      }
    }
  }
}
</style>
